import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** The current route. A change clears the error screen, so a crash on one
   *  page never traps the lawyer there. */
  resetKey?: string;
  /** 'page' (default) fills the screen — the last-resort boundary around the
   *  whole app. 'inline' is the compact card the app layout puts around the
   *  routed page, so a page crash leaves the header and sidebar standing. */
  variant?: 'page' | 'inline';
}

interface State {
  hasError: boolean;
  error: Error | null;
  ref: string | null;
  /** A stale-build chunk error the boundary is about to fix by reloading. */
  reloading?: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

/** Short code QA can quote; the same code is in the server log line. */
function newRef(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 6; i += 1) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return `E-${out}`;
}

function currentBuild(): string {
  const main = Array.from(document.scripts).map((s) => s.src).find((src) => /\/main\.[a-f0-9]+\.js$/.test(src));
  return main ? main.split('/').pop() || '' : '';
}

/**
 * A tab opened before a frontend deploy still runs the old main.js, which
 * names code-split chunks the new deploy no longer serves (404). The first page
 * it lazy-loads then throws ChunkLoadError — dev, 2026-10-17, Cause List,
 * ref E-D449K4. Nothing is wrong with the page; the tab is out of date, and
 * "Try Again" cannot help because the file is gone. Reloading fetches the new
 * build.
 */
export function isStaleChunkError(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null;
  const text = `${e?.name ?? ''} ${e?.message ?? ''}`;
  return /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed/i.test(text);
}

const RELOAD_KEY = 'stale-chunk-reload-at';
const RELOAD_WINDOW_MS = 30_000;

/** True when no stale-chunk reload happened in the last 30s. The window is
 *  what stops a loop: if the reloaded page still cannot load the chunk, the
 *  problem is not a stale tab, and the lawyer gets the error screen. */
export function mayReloadForNewBuild(now: number = Date.now()): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
    return now - last > RELOAD_WINDOW_MS;
  } catch {
    return false; // storage blocked: never risk a reload loop
  }
}

function markReloadForNewBuild(now: number = Date.now()): void {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // mayReloadForNewBuild already returned false when storage is blocked
  }
}

/** Fire-and-forget: a failing report must never break the error screen. */
function reportClientError(ref: string, error: Error, errorInfo: ErrorInfo): void {
  try {
    const body = JSON.stringify({
      ref,
      message: String(error?.message ?? error).slice(0, 2000),
      stack: String(error?.stack ?? '').slice(0, 20000),
      component_stack: String(errorInfo?.componentStack ?? '').slice(0, 20000),
      path: window.location.pathname, // never the query string — it carries ids
      build: currentBuild(),
    });
    void fetch(`${API_URL}/api/v1/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // reporting is best-effort
  }
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, ref: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      ref: newRef(),
      reloading: isStaleChunkError(error) && mayReloadForNewBuild(),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    reportClientError(this.state.ref ?? newRef(), error, errorInfo);
    if (this.state.reloading) {
      markReloadForNewBuild();
      window.location.reload();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, ref: null, reloading: false });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, ref: null, reloading: false });
  };

  handleReload = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError && this.state.reloading) {
      return (
        <div role="status" className="py-16 text-center text-sm text-parchment-muted">
          A newer version of iAdvocate is available — loading it…
        </div>
      );
    }
    if (this.state.hasError && this.props.variant === 'inline') {
      return (
        <div
          role="alert"
          data-testid="page-error-inline"
          className="max-w-xl mx-auto my-10 rounded-lg border border-red-200 bg-white p-6 text-center"
        >
          <AlertTriangle className="h-6 w-6 text-red-600 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-1">This page could not be shown</h2>
          <p className="text-sm text-gray-600 mb-4">
            An unexpected error occurred on this page. The rest of the app still works — try again or open another page from the sidebar.
          </p>
          {this.state.ref && (
            <p className="text-xs text-gray-500 mb-4">
              Reference: <span className="font-mono">{this.state.ref}</span> — quote this when reporting the issue.
            </p>
          )}
          <button
            onClick={this.handleRetry}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              An unexpected error occurred. Please try again or return to the dashboard.
            </p>
            {this.state.ref && (
              <p className="text-xs text-gray-500 dark:text-gray-400 -mt-6 mb-8">
                Reference: <span className="font-mono">{this.state.ref}</span> — quote this when reporting the issue.
              </p>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 px-4 py-2 bg-navy-700 dark:bg-gold-500 text-white dark:text-white rounded-lg text-sm font-medium hover:bg-navy-800 dark:hover:bg-legal-green-500 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-8 text-left">
                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                  Error Details
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded text-xs text-red-600 dark:text-red-400 overflow-auto max-h-48">
                  {this.state.error.message}
                  {'\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
